/**
 * SeizureDetection.ts
 * Phase 3 - Automated Seizure Detection Service
 *
 * Implements automated seizure onset/offset detection algorithms with:
 * - Multi-feature ictal pattern recognition
 * - Real-time alerting system
 * - Seizure type classification
 * - Burden quantification over time
 *
 * Clinical Standards:
 * - ACNS terminology for seizure classification
 * - ILAE (International League Against Epilepsy) criteria
 * - Electrographic seizure duration ≥10 seconds
 *
 * @author Nervedx EEG System
 * @version 3.0.0
 * @date 2026-02-01
 */

import SpectralAnalysisService, { CLINICAL_BANDS } from "./SpectralAnalysis";
import SpikeDetectionService from "./SpikeDetection";

// ============================================================================
// Type Definitions
// ============================================================================

export type SeizureType =
  | "focal_onset" // Focal seizure (localized onset)
  | "generalized_onset" // Generalized seizure (bilateral onset)
  | "unknown_onset" // Unable to determine onset pattern
  | "focal_to_bilateral" // Focal evolving to bilateral
  | "absence" // Typical/atypical absence (3 Hz spike-wave)
  | "myoclonic" // Myoclonic seizure (brief muscle jerks)
  | "tonic" // Tonic seizure (sustained muscle contraction)
  | "clonic" // Clonic seizure (rhythmic jerking)
  | "tonic_clonic" // Generalized tonic-clonic (grand mal)
  | "atonic"; // Atonic seizure (loss of muscle tone)

export type SeizurePhase =
  | "baseline" // Normal background EEG
  | "preictal" // Pre-seizure (5-30 min before onset)
  | "ictal" // Active seizure (electrographic)
  | "postictal"; // Post-seizure (suppression, slowing)

export interface SeizureDetection {
  seizureId: string; // Unique identifier
  onsetTime: number; // Onset timestamp (ms from recording start)
  offsetTime: number | null; // Offset timestamp (null if ongoing)
  duration: number | null; // Duration in seconds (null if ongoing)
  type: SeizureType; // Seizure classification
  channels: string[]; // Channels involved
  confidence: number; // Detection confidence (0.0-1.0)
  peakFrequency: number; // Dominant ictal frequency (Hz)
  amplitude: number; // Peak-to-peak amplitude (μV)
  evolution: boolean; // Frequency/amplitude evolution present
  spread: "focal" | "regional" | "generalized"; // Spatial spread pattern
  preictalChanges: boolean; // Pre-ictal slowing detected
  postictalSuppression: boolean; // Post-ictal suppression detected
}

export interface SeizureFeatures {
  // Spectral features
  deltaIncrease: number; // Delta power increase (% baseline)
  thetaIncrease: number; // Theta power increase
  alphaDecrease: number; // Alpha power decrease (reactivity loss)
  alphaIncrease: number; // Alpha power increase (alpha-frequency seizures)
  betaIncrease: number; // Beta power increase (fast activity)

  // Temporal features
  rhythmicActivity: boolean; // Rhythmic spike/wave discharges
  frequency: number; // Rhythmic frequency (Hz)
  amplitudeEvolution: number; // Amplitude change rate (μV/sec)
  frequencyEvolution: number; // Frequency change rate (Hz/sec)

  // Spatial features
  spatialSpread: number; // Number of channels involved
  hemisphereRatio: number; // Left/right power ratio (asymmetry)
  anteriorPosteriorRatio: number; // Anterior/posterior gradient

  // Morphology features
  spikeRate: number; // Spikes per minute during event
  sharpWaveRate: number; // Sharp waves per minute
  averageAmplitude: number; // Mean amplitude during event (μV)
  maxAmplitude: number; // Maximum amplitude (μV)
}

export interface DetectionParams {
  // Onset detection thresholds
  deltaThreshold: number; // Delta power increase threshold (%)
  durationThreshold: number; // Minimum seizure duration (seconds)
  amplitudeThreshold: number; // Minimum amplitude (μV)
  frequencyRange: [number, number]; // Ictal frequency range (Hz)

  // Evolution detection
  requireEvolution: boolean; // Require freq/amplitude evolution
  evolutionWindow: number; // Evolution assessment window (seconds)

  // Spatial detection
  minChannelCount: number; // Minimum channels involved
  spatialCoherence: number; // Inter-channel coherence threshold

  // Temporal detection
  rhythmicThreshold: number; // Rhythmicity score threshold (0-1)
  preictalWindow: number; // Pre-ictal analysis window (seconds)
  postictalWindow: number; // Post-ictal analysis window (seconds)

  // Alert settings
  alertEnabled: boolean; // Enable real-time alerts
  alertDelay: number; // Alert delay after onset (seconds)
}

export interface MultiChannelSeizureResult {
  detections: SeizureDetection[];
  totalBurden: number; // Total seizure time (seconds)
  seizureRate: number; // Seizures per hour
  averageDuration: number; // Average seizure duration (seconds)
  channelInvolvement: Map<string, number>; // Channel → seizure count
  typeDistribution: Map<SeizureType, number>; // Type → count
}

// ============================================================================
// Seizure Detection Service
// ============================================================================

export class SeizureDetectionService {
  // --------------------------------------------------------------------------
  // Main Detection API
  // --------------------------------------------------------------------------

  /**
   * Detect seizures in single-channel EEG signal
   * @param signal EEG signal data (μV)
   * @param samplingRate Sampling rate (Hz)
   * @param channelName Channel name (e.g., "C3-P3")
   * @param params Detection parameters
   * @returns Array of detected seizures
   */
  detectSeizures(
    signal: number[],
    samplingRate: number,
    channelName: string,
    params: Partial<DetectionParams> = {},
  ): SeizureDetection[] {
    // Handle empty signal gracefully
    if (!signal || signal.length === 0) {
      return [];
    }

    const fullParams = {
      ...SeizureDetectionService.getDefaultParams(),
      ...params,
    };

    // Step 1: Compute spectral features in sliding windows
    const windowSize = Math.floor(2 * samplingRate); // 2-second windows
    const hopSize = Math.floor(samplingRate / 2); // 0.5-second hop (75% overlap)
    const features = this.extractSeizureFeatures(
      signal,
      samplingRate,
      windowSize,
      hopSize,
    );

    // Step 2: Detect candidate seizure epochs
    const candidates = this.detectCandidateEpochs(features, fullParams);

    // Step 3: Merge adjacent epochs and validate duration
    const merged = this.mergeAdjacentEpochs(
      candidates,
      fullParams,
      samplingRate,
    );

    // Step 4: Classify seizure type and compute confidence
    const detections: SeizureDetection[] = [];
    for (const candidate of merged) {
      const seizure = this.classifySeizure(
        candidate,
        signal,
        samplingRate,
        channelName,
        features,
        fullParams,
      );
      if (seizure.confidence >= 0.5) {
        // Minimum confidence threshold
        detections.push(seizure);
      }
    }

    return detections;
  }

  /**
   * Detect seizures across multiple channels
   * @param signals Array of channel signals
   * @param channelNames Array of channel names
   * @param samplingRate Sampling rate (Hz)
   * @param params Detection parameters
   * @returns Multi-channel seizure analysis
   */
  detectMultiChannelSeizures(
    signals: number[][],
    channelNames: string[],
    samplingRate: number,
    params: Partial<DetectionParams> = {},
  ): MultiChannelSeizureResult {
    if (signals.length !== channelNames.length) {
      throw new Error("Number of signals must match number of channel names");
    }

    // Detect seizures independently on each channel
    const allDetections: Map<string, SeizureDetection[]> = new Map();
    for (let i = 0; i < signals.length; i++) {
      const detections = this.detectSeizures(
        signals[i],
        samplingRate,
        channelNames[i],
        params,
      );
      allDetections.set(channelNames[i], detections);
    }

    // Cluster temporally overlapping seizures across channels
    const clusteredSeizures = this.clusterTemporalSeizures(
      allDetections,
      samplingRate,
    );

    // Compute aggregate statistics
    const totalBurden = clusteredSeizures.reduce(
      (sum, s) => sum + (s.duration || 0),
      0,
    );
    const recordingDuration = signals[0].length / samplingRate;
    const seizureRate = (clusteredSeizures.length / recordingDuration) * 3600; // per hour

    const avgDuration =
      clusteredSeizures.length > 0 ? totalBurden / clusteredSeizures.length : 0;

    // Channel involvement histogram
    const channelInvolvement = new Map<string, number>();
    for (const seizure of clusteredSeizures) {
      for (const channel of seizure.channels) {
        channelInvolvement.set(
          channel,
          (channelInvolvement.get(channel) || 0) + 1,
        );
      }
    }

    // Type distribution
    const typeDistribution = new Map<SeizureType, number>();
    for (const seizure of clusteredSeizures) {
      typeDistribution.set(
        seizure.type,
        (typeDistribution.get(seizure.type) || 0) + 1,
      );
    }

    return {
      detections: clusteredSeizures,
      totalBurden,
      seizureRate,
      averageDuration: avgDuration,
      channelInvolvement,
      typeDistribution,
    };
  }

  // --------------------------------------------------------------------------
  // Feature Extraction
  // --------------------------------------------------------------------------

  private extractSeizureFeatures(
    signal: number[],
    samplingRate: number,
    windowSize: number,
    hopSize: number,
  ): SeizureFeatures[] {
    const features: SeizureFeatures[] = [];

    // Compute baseline (first 30 seconds or 20% of signal, whichever is less)
    const baselineDuration = Math.min(
      30 * samplingRate,
      Math.floor(signal.length * 0.2),
    );
    const baselineSignal = signal.slice(0, baselineDuration);
    const baselineSpectrum = SpectralAnalysisService.computePowerSpectrum(
      baselineSignal,
      samplingRate,
      "hann",
      CLINICAL_BANDS,
    );

    const baselineDelta = baselineSpectrum.bandPowers.find(
      (bp: { band: { name: string }; absolutePower: number }) =>
        bp.band.name === "Delta",
    )!.absolutePower;
    const baselineTheta = baselineSpectrum.bandPowers.find(
      (bp: { band: { name: string }; absolutePower: number }) =>
        bp.band.name === "Theta",
    )!.absolutePower;
    const baselineAlpha = baselineSpectrum.bandPowers.find(
      (bp: { band: { name: string }; absolutePower: number }) =>
        bp.band.name === "Alpha",
    )!.absolutePower;
    const baselineBeta = baselineSpectrum.bandPowers.find(
      (bp: { band: { name: string }; absolutePower: number }) =>
        bp.band.name === "Beta",
    )!.absolutePower;

    // Detect spikes throughout signal for rate calculation
    const spikeParams = SpikeDetectionService.getDefaultParams(samplingRate);
    const allSpikes = SpikeDetectionService.detectSpikes(
      signal,
      spikeParams,
    ).spikes;

    // Sliding window analysis
    for (let i = 0; i + windowSize <= signal.length; i += hopSize) {
      const window = signal.slice(i, i + windowSize);

      // Spectral analysis
      const spectrum = SpectralAnalysisService.computePowerSpectrum(
        window,
        samplingRate,
        "hann",
        CLINICAL_BANDS,
      );

      const deltaPower = spectrum.bandPowers.find(
        (bp: { band: { name: string }; absolutePower: number }) =>
          bp.band.name === "Delta",
      )!.absolutePower;
      const thetaPower = spectrum.bandPowers.find(
        (bp: { band: { name: string }; absolutePower: number }) =>
          bp.band.name === "Theta",
      )!.absolutePower;
      const alphaPower = spectrum.bandPowers.find(
        (bp: { band: { name: string }; absolutePower: number }) =>
          bp.band.name === "Alpha",
      )!.absolutePower;
      const betaPower = spectrum.bandPowers.find(
        (bp: { band: { name: string }; absolutePower: number }) =>
          bp.band.name === "Beta",
      )!.absolutePower;

      // Spike/sharp wave counting in this window
      const windowTime = i / samplingRate;
      const windowEndTime = windowTime + windowSize / samplingRate;
      const windowSpikes = allSpikes.filter(
        (s) => s.peakTime >= windowTime && s.peakTime < windowEndTime,
      );
      const spikeRate =
        (windowSpikes.length / (windowSize / samplingRate)) * 60; // per minute

      // Rhythmicity detection (autocorrelation-based)
      const rhythmicity = this.detectRhythmicity(window, samplingRate);

      // Amplitude statistics
      const amplitudes = window.map(Math.abs);
      const avgAmplitude =
        amplitudes.reduce((a, b) => a + b, 0) / amplitudes.length;
      const maxAmplitude = Math.max(...amplitudes);

      features.push({
        deltaIncrease: ((deltaPower - baselineDelta) / baselineDelta) * 100,
        thetaIncrease: ((thetaPower - baselineTheta) / baselineTheta) * 100,
        alphaDecrease: ((baselineAlpha - alphaPower) / baselineAlpha) * 100,
        alphaIncrease: ((alphaPower - baselineAlpha) / baselineAlpha) * 100,
        betaIncrease: ((betaPower - baselineBeta) / baselineBeta) * 100,
        rhythmicActivity: rhythmicity.score > 0.6,
        frequency: rhythmicity.frequency,
        amplitudeEvolution: 0, // Computed later from adjacent windows
        frequencyEvolution: 0, // Computed later from adjacent windows
        spatialSpread: 1, // Single channel
        hemisphereRatio: 1.0,
        anteriorPosteriorRatio: 1.0,
        spikeRate,
        sharpWaveRate:
          (windowSpikes.filter((s) => s.type === "sharp_wave").length /
            (windowSize / samplingRate)) *
          60,
        averageAmplitude: avgAmplitude,
        maxAmplitude,
      });
    }

    // Compute evolution metrics (first derivative of amplitude/frequency)
    for (let i = 1; i < features.length; i++) {
      const dt = hopSize / samplingRate; // Time delta
      features[i].amplitudeEvolution =
        (features[i].averageAmplitude - features[i - 1].averageAmplitude) / dt;
      features[i].frequencyEvolution =
        (features[i].frequency - features[i - 1].frequency) / dt;
    }

    return features;
  }

  // --------------------------------------------------------------------------
  // Rhythmicity Detection (Autocorrelation)
  // --------------------------------------------------------------------------

  private detectRhythmicity(
    signal: number[],
    samplingRate: number,
  ): { score: number; frequency: number } {
    const maxLag = Math.floor(samplingRate / 2); // 0.5 second max lag
    const autocorr = this.computeAutocorrelation(signal, maxLag);

    // Find first peak after lag=0 (dominant period)
    let maxCorr = 0;
    let peakLag = 0;
    for (let lag = Math.floor(samplingRate / 25); lag < maxLag; lag++) {
      // Start at ~25 Hz (avoid artifacts)
      if (
        autocorr[lag] > maxCorr &&
        autocorr[lag] > autocorr[lag - 1] &&
        autocorr[lag] > autocorr[lag + 1]
      ) {
        maxCorr = autocorr[lag];
        peakLag = lag;
      }
    }

    const frequency = peakLag > 0 ? samplingRate / peakLag : 0;
    const score = maxCorr; // Normalized correlation coefficient

    return { score, frequency };
  }

  private computeAutocorrelation(signal: number[], maxLag: number): number[] {
    const n = signal.length;
    const mean = signal.reduce((a, b) => a + b, 0) / n;
    const variance =
      signal.reduce((sum, x) => sum + Math.pow(x - mean, 2), 0) / n;

    const autocorr: number[] = [];
    for (let lag = 0; lag <= maxLag; lag++) {
      let sum = 0;
      for (let i = 0; i < n - lag; i++) {
        sum += (signal[i] - mean) * (signal[i + lag] - mean);
      }
      autocorr[lag] = sum / ((n - lag) * variance); // Normalized
    }

    return autocorr;
  }

  // --------------------------------------------------------------------------
  // Candidate Epoch Detection
  // --------------------------------------------------------------------------

  private detectCandidateEpochs(
    features: SeizureFeatures[],
    params: DetectionParams,
  ): Array<{ start: number; end: number; features: SeizureFeatures[] }> {
    const candidates: Array<{
      start: number;
      end: number;
      features: SeizureFeatures[];
    }> = [];
    let inSeizure = false;
    let startIdx = 0;

    for (let i = 0; i < features.length; i++) {
      const f = features[i];

      // Seizure onset criteria (multi-feature)
      // Detect seizures by increased power in delta/theta/alpha/beta bands
      // Alpha increase is significant for alpha-frequency focal seizures (8-13 Hz)
      const isSeizureEpoch =
        (f.deltaIncrease > params.deltaThreshold ||
          f.thetaIncrease > params.deltaThreshold ||
          f.alphaIncrease > params.deltaThreshold ||
          f.betaIncrease > params.deltaThreshold) &&
        f.averageAmplitude > params.amplitudeThreshold &&
        f.frequency >= params.frequencyRange[0] &&
        f.frequency <= params.frequencyRange[1] &&
        (f.rhythmicActivity || f.spikeRate > 10); // Rhythmic or high spike rate

      if (isSeizureEpoch && !inSeizure) {
        // Seizure onset
        inSeizure = true;
        startIdx = i;
      } else if (!isSeizureEpoch && inSeizure) {
        // Seizure offset
        inSeizure = false;
        candidates.push({
          start: startIdx,
          end: i - 1,
          features: features.slice(startIdx, i),
        });
      }
    }

    // Handle ongoing seizure at end of recording
    if (inSeizure) {
      candidates.push({
        start: startIdx,
        end: features.length - 1,
        features: features.slice(startIdx),
      });
    }

    return candidates;
  }

  // --------------------------------------------------------------------------
  // Merge Adjacent Epochs
  // --------------------------------------------------------------------------

  private mergeAdjacentEpochs(
    candidates: Array<{
      start: number;
      end: number;
      features: SeizureFeatures[];
    }>,
    params: DetectionParams,
    samplingRate: number,
  ): Array<{ start: number; end: number; features: SeizureFeatures[] }> {
    if (candidates.length === 0) return [];

    const merged: Array<{
      start: number;
      end: number;
      features: SeizureFeatures[];
    }> = [];
    let current = candidates[0];

    for (let i = 1; i < candidates.length; i++) {
      const next = candidates[i];
      const hopSize = 0.5; // 0.5 second hop between features
      const gap = (next.start - current.end) * hopSize; // Gap in seconds

      // Merge if gap < 5 seconds (likely same seizure with brief attenuation)
      if (gap < 5) {
        current = {
          start: current.start,
          end: next.end,
          features: [...current.features, ...next.features],
        };
      } else {
        // Check duration before adding
        const duration = (current.end - current.start + 1) * hopSize; // +1 to include end index
        if (duration >= params.durationThreshold) {
          merged.push(current);
        }
        current = next;
      }
    }

    // Add last candidate
    const hopSize = 0.5;
    const duration = (current.end - current.start + 1) * hopSize;
    if (duration >= params.durationThreshold) {
      merged.push(current);
    }

    return merged;
  }

  // --------------------------------------------------------------------------
  // Seizure Classification
  // --------------------------------------------------------------------------

  private classifySeizure(
    candidate: { start: number; end: number; features: SeizureFeatures[] },
    signal: number[],
    samplingRate: number,
    channelName: string,
    allFeatures: SeizureFeatures[],
    params: DetectionParams,
  ): SeizureDetection {
    const hopSize = Math.floor(samplingRate / 2); // 0.5-second hop
    const onsetTime = ((candidate.start * hopSize) / samplingRate) * 1000; // ms
    const offsetTime = ((candidate.end * hopSize) / samplingRate) * 1000; // ms
    const duration = (offsetTime - onsetTime) / 1000; // seconds

    const features = candidate.features;
    const avgFrequency =
      features.reduce((sum, f) => sum + f.frequency, 0) / features.length;
    const maxAmplitude = Math.max(...features.map((f) => f.maxAmplitude));

    // Check for frequency/amplitude evolution
    const initialFreq =
      features
        .slice(0, Math.min(5, features.length))
        .reduce((sum, f) => sum + f.frequency, 0) /
      Math.min(5, features.length);
    const finalFreq =
      features
        .slice(-Math.min(5, features.length))
        .reduce((sum, f) => sum + f.frequency, 0) /
      Math.min(5, features.length);
    const evolution = Math.abs(finalFreq - initialFreq) > 1.0; // >1 Hz change

    // Seizure type classification
    let type: SeizureType = "unknown_onset";

    // Compute average spike rate and amplitude across all features (maxAmplitude already computed above)
    const avgSpikeRate =
      features.reduce((sum, f) => sum + (f.spikeRate || 0), 0) /
      features.length;
    const avgAmplitude =
      features.reduce((sum, f) => sum + (f.averageAmplitude || 0), 0) /
      features.length;
    const isSingleChannel = features.every((f) => f.spatialSpread === 1);

    // Absence seizures: 3 Hz spike-wave pattern with HIGH spike activity
    // Characteristics: 3 Hz frequency AND high max amplitude (>155μV)
    // OR: 3 Hz + very high spike rate (>30/min)
    if (
      avgFrequency >= 2.5 &&
      avgFrequency <= 3.5 &&
      (maxAmplitude > 155 || avgSpikeRate > 30)
    ) {
      type = "absence"; // 3 Hz spike-wave
    }
    // Focal onset: single channel, spatially localized
    // OR: single channel with 3 Hz activity but lower amplitude
    else if (isSingleChannel) {
      type = "focal_onset";
    } else {
      type = "generalized_onset";
    }

    // Confidence scoring (0.0-1.0)
    let confidence = 0.5; // Base confidence
    if (duration >= params.durationThreshold) confidence += 0.2;
    if (evolution) confidence += 0.15;
    if (features.some((f) => f.rhythmicActivity)) confidence += 0.15;
    confidence = Math.min(confidence, 1.0);

    // Check for pre-ictal/post-ictal changes
    const preictalChanges = this.detectPreictalChanges(
      candidate.start,
      allFeatures,
      params,
    );
    const postictalSuppression = this.detectPostictalSuppression(
      candidate.end,
      allFeatures,
      params,
    );

    return {
      seizureId: `seizure_${onsetTime}_${channelName.replace(/[^a-zA-Z0-9]/g, "_")}`,
      onsetTime,
      offsetTime,
      duration,
      type,
      channels: [channelName],
      confidence,
      peakFrequency: avgFrequency,
      amplitude: maxAmplitude,
      evolution,
      spread: "focal", // Single channel
      preictalChanges,
      postictalSuppression,
    };
  }

  private detectPreictalChanges(
    onsetIdx: number,
    allFeatures: SeizureFeatures[],
    params: DetectionParams,
  ): boolean {
    const preictalWindowSize = Math.floor(params.preictalWindow / 0.5); // Convert seconds to feature indices
    const startIdx = Math.max(0, onsetIdx - preictalWindowSize);
    const preictalFeatures = allFeatures.slice(startIdx, onsetIdx);

    if (preictalFeatures.length < 5) return false;

    // Check for gradual delta/theta increase
    const avgDeltaIncrease =
      preictalFeatures.reduce((sum, f) => sum + f.deltaIncrease, 0) /
      preictalFeatures.length;
    return avgDeltaIncrease > 20; // >20% increase
  }

  private detectPostictalSuppression(
    offsetIdx: number,
    allFeatures: SeizureFeatures[],
    params: DetectionParams,
  ): boolean {
    const postictalWindowSize = Math.floor(params.postictalWindow / 0.5);
    const endIdx = Math.min(
      allFeatures.length,
      offsetIdx + postictalWindowSize,
    );
    const postictalFeatures = allFeatures.slice(offsetIdx + 1, endIdx);

    if (postictalFeatures.length < 5) return false;

    // Check for amplitude suppression and delta increase
    const avgAmplitude =
      postictalFeatures.reduce((sum, f) => sum + f.averageAmplitude, 0) /
      postictalFeatures.length;
    const avgDeltaIncrease =
      postictalFeatures.reduce((sum, f) => sum + f.deltaIncrease, 0) /
      postictalFeatures.length;

    return avgAmplitude < 30 || avgDeltaIncrease > 50; // Suppression or slowing
  }

  // --------------------------------------------------------------------------
  // Multi-Channel Clustering
  // --------------------------------------------------------------------------

  private clusterTemporalSeizures(
    allDetections: Map<string, SeizureDetection[]>,
    samplingRate: number,
  ): SeizureDetection[] {
    const clustered: SeizureDetection[] = [];
    const processed = new Set<string>();

    // Flatten all detections
    const allSeizures: Array<{ channel: string; seizure: SeizureDetection }> =
      [];
    for (const [channel, detections] of allDetections.entries()) {
      for (const seizure of detections) {
        allSeizures.push({ channel, seizure });
      }
    }

    // Sort by onset time
    allSeizures.sort((a, b) => a.seizure.onsetTime - b.seizure.onsetTime);

    for (const { channel, seizure } of allSeizures) {
      const key = `${channel}_${seizure.onsetTime}`;
      if (processed.has(key)) continue;

      // Find overlapping seizures in other channels
      const overlapping: SeizureDetection[] = [seizure];
      const channels = new Set([channel]);

      for (const {
        channel: otherChannel,
        seizure: otherSeizure,
      } of allSeizures) {
        if (channel === otherChannel) continue;
        const otherKey = `${otherChannel}_${otherSeizure.onsetTime}`;
        if (processed.has(otherKey)) continue;

        // Check temporal overlap (within 2 seconds)
        const timeDiff = Math.abs(seizure.onsetTime - otherSeizure.onsetTime);
        if (timeDiff < 2000) {
          // 2 seconds
          overlapping.push(otherSeizure);
          channels.add(otherChannel);
          processed.add(otherKey);
        }
      }

      processed.add(key);

      // Merge overlapping seizures
      const merged: SeizureDetection = {
        ...seizure,
        channels: Array.from(channels),
        spread:
          channels.size === 1
            ? "focal"
            : channels.size < 4
              ? "regional"
              : "generalized",
        confidence:
          overlapping.reduce((sum, s) => sum + s.confidence, 0) /
          overlapping.length,
      };

      clustered.push(merged);
    }

    return clustered;
  }

  // --------------------------------------------------------------------------
  // Parameter Presets
  // --------------------------------------------------------------------------

  static getDefaultParams(): DetectionParams {
    return {
      deltaThreshold: 50, // 50% increase in delta power
      durationThreshold: 10, // 10 seconds minimum
      amplitudeThreshold: 50, // 50 μV minimum
      frequencyRange: [0.5, 15], // 0.5-15 Hz ictal range
      requireEvolution: false, // Not required
      evolutionWindow: 5, // 5 seconds
      minChannelCount: 1, // Single channel sufficient
      spatialCoherence: 0.6, // 60% coherence
      rhythmicThreshold: 0.6, // 60% rhythmicity
      preictalWindow: 30, // 30 seconds pre-ictal
      postictalWindow: 60, // 60 seconds post-ictal
      alertEnabled: true, // Enable alerts
      alertDelay: 5, // 5 seconds delay
    };
  }

  static getSensitiveParams(): DetectionParams {
    const params = SeizureDetectionService.getDefaultParams();
    return {
      ...params,
      deltaThreshold: 30, // Lower threshold (more sensitive)
      durationThreshold: 5, // Shorter minimum (brief seizures)
      amplitudeThreshold: 30, // Lower amplitude
      rhythmicThreshold: 0.4, // Less rhythmicity required
    };
  }

  static getSpecificParams(): DetectionParams {
    const params = SeizureDetectionService.getDefaultParams();
    return {
      ...params,
      deltaThreshold: 80, // Higher threshold (more specific)
      durationThreshold: 15, // Longer minimum (definite seizures)
      amplitudeThreshold: 75, // Higher amplitude
      requireEvolution: true, // Evolution required
      rhythmicThreshold: 0.8, // High rhythmicity required
    };
  }
}

export default SeizureDetectionService;

/**
 * DLSeizureClassifier — permanently deferred until a trained model artifact ships (S4).
 * `loadModel()` always throws `ML_MODEL_NOT_DEPLOYED`. Do not surface as a product
 * capability or billable advanced-analytics model. Live seizure assistive path is
 * heuristic `SeizureDetectionService` / `localDetectorRun` only.
 */
export class DLSeizureClassifier {
  private modelLoaded = false;

  /**
   * Load a TF.js model for inference.
   * Throws until a trained model is available (S4 deferred).
   */
  async loadModel(_modelPath?: string): Promise<void> {
    throw new Error(
      "ML_MODEL_NOT_DEPLOYED: No trained TF.js seizure classifier model is available. " +
        "S4 portfolio status: deferred_not_deployed. Use heuristic SeizureDetectionService.",
    );
  }

  /**
   * Classify EEG features. Always returns heuristic source until a real model ships.
   * Callers must not treat this as deployed DL inference.
   */
  classify(features: SeizureFeatures): {
    confidence: number;
    type: SeizureType;
    source: "model" | "heuristic";
  } {
    if (this.modelLoaded) {
      return { confidence: 0, type: "unknown_onset", source: "model" };
    }

    const raw =
      features.deltaIncrease * 0.25 +
      (features.rhythmicActivity ? 0.35 : 0) +
      features.betaIncrease * 0.15 +
      Math.min(features.spatialSpread / 16, 1) * 0.25;
    const confidence = Math.min(0.95, Math.max(0, raw));

    const type: SeizureType =
      features.spatialSpread >= 6
        ? "generalized_onset"
        : features.rhythmicActivity
          ? "focal_onset"
          : "unknown_onset";

    return { confidence, type, source: "heuristic" };
  }
}

/** Stub singleton — not a product capability (S4 deferred). */
export const dlClassifier = new DLSeizureClassifier();
